import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import gsap from 'gsap'

type Phase = 'count' | 'split' | 'done'

interface LoaderProps {
  onComplete: () => void
}

export function Loader({ onComplete }: LoaderProps) {
  const [phase, setPhase] = useState<Phase>('count')
  const [display, setDisplay] = useState(0)
  const countRef = useRef({ val: 0 })

  useEffect(() => {
    // Check if already loaded this session
    if (sessionStorage.getItem('loaded') === '1') {
      setPhase('done')
      onComplete()
      return
    }

    const tl = gsap.timeline({
      onComplete: () => {
        setTimeout(() => setPhase('split'), 200)
      }
    })

    tl.to(countRef.current, {
      val: 100,
      duration: 1.6,
      ease: 'power2.inOut',
      onUpdate: () => setDisplay(Math.round(countRef.current.val))
    })

    return () => {
      tl.kill()
    }
  }, [onComplete])

  useEffect(() => {
    if (phase === 'split') {
      const timer = setTimeout(() => {
        setPhase('done')
        sessionStorage.setItem('loaded', '1')
        onComplete()
      }, 900)
      return () => clearTimeout(timer)
    }
  }, [phase, onComplete])

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9990,
            background: 'var(--black)'
          }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Counter display */}
          {phase === 'count' && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px'
              }}
            >
              {/* Number */}
              <div
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: 'clamp(6rem, 20vw, 16rem)',
                  color: 'var(--lime)',
                  lineHeight: 1,
                  letterSpacing: '-0.02em'
                }}
              >
                {display}
              </div>

              {/* Brand */}
              <div
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: 'clamp(1.2rem, 3vw, 2rem)',
                  color: 'var(--white-30)',
                  letterSpacing: '0.3em'
                }}
              >
                ASHU CLEANING SERVICE
              </div>

              {/* Amharic */}
              <div
                style={{
                  fontFamily: 'DM Sans',
                  fontSize: '1rem',
                  color: 'var(--lime)',
                  opacity: 0.6
                }}
              >
                አሹ የፅዳት አገልግሎት
              </div>

              {/* Progress bar */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: '2px',
                  background: 'var(--lime)',
                  width: `${display}%`,
                  transition: 'width 0.05s linear',
                  boxShadow: '0 0 12px var(--lime)'
                }}
              />
            </div>
          )}

          {/* Split panels */}
          {phase === 'split' && (
            <>
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'var(--black)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  paddingBottom: '4px'
                }}
                initial={{ y: 0 }}
                animate={{ y: '-100%' }}
                transition={{ duration: 0.7, ease: [0.87, 0, 0.13, 1] }}
              >
                <div
                  style={{
                    fontFamily: 'Bebas Neue',
                    fontSize: 'clamp(6rem, 20vw, 16rem)',
                    color: 'var(--lime)',
                    lineHeight: 1
                  }}
                >
                  100
                </div>
              </motion.div>

              <motion.div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'var(--black)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  paddingTop: '4px'
                }}
                initial={{ y: 0 }}
                animate={{ y: '100%' }}
                transition={{ duration: 0.7, ease: [0.87, 0, 0.13, 1] }}
              >
                <div
                  style={{
                    fontFamily: 'Bebas Neue',
                    fontSize: 'clamp(6rem, 20vw, 16rem)',
                    color: 'var(--lime)',
                    lineHeight: 1
                  }}
                >
                  100
                </div>
              </motion.div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
